import { useEffect, useState } from "react";
import { App, Button, Form, Input, Modal, Select, Space, Table, Tag } from "antd";
import { EMPTY_PERSON_NAME_MESSAGE, LONG_PERSON_NAME_MESSAGE, PERSON_NAME_MAX_LENGTH, type RoleCode } from "@shoe-trace/shared";
import { changeUserRole, createUser, disableUser, fetchMe, listUsers, renameUser, resetPassword, type AccountRow } from "../api/client";
import { formatWhen, LoadFail, PageSpin } from "../components/widgets";
import { useSession } from "../session";

interface CreateValues {
  employeeNo: string;
  name: string;
  role: RoleCode;
  password: string;
}

interface RenameValues {
  name: string;
}

interface RoleValues {
  role: RoleCode;
}

const ROLE_OPTIONS: Array<{ value: RoleCode; label: string }> = [
  { value: "OPERATOR", label: "作业员" },
  { value: "SUPERVISOR", label: "主管" },
  { value: "ADMIN", label: "管理员" },
];

function isFieldError(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("errorFields" in error)) return false;
  return Array.isArray(error.errorFields);
}

export function Accounts(): JSX.Element {
  const { message } = App.useApp();
  const { user, replaceUser } = useSession();
  const [rows, setRows] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [disableId, setDisableId] = useState<string | null>(null);
  const [resetId, setResetId] = useState<string | null>(null);
  const [renameRow, setRenameRow] = useState<AccountRow | null>(null);
  const [roleRow, setRoleRow] = useState<AccountRow | null>(null);
  const [tempPassword, setTempPassword] = useState("");
  const [form] = Form.useForm<CreateValues>();
  const [renameForm] = Form.useForm<RenameValues>();
  const [roleForm] = Form.useForm<RoleValues>();

  function load(): void {
    setLoading(true);
    void listUsers()
      .then((next) => {
        setRows(next);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  if (loading && rows.length === 0) return <PageSpin tip="正在读取账号…" />;
  if (failed) return <LoadFail title="账号没有改成。" onRetry={load} />;

  const shown = includeDisabled ? rows : rows.filter((row) => row.status === "ACTIVE");

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => setCreateOpen(true)}>开通这个账号</Button>
        <Button onClick={() => setIncludeDisabled((current) => !current)}>{includeDisabled ? "只看在职" : "含已停用"}</Button>
      </Space>
      <Table
        rowKey="id"
        dataSource={shown}
        locale={{ emptyText: "还没有账号。先开通作业员和主管，每个人单独一个工号。" }}
        columns={[
          { title: "工号", dataIndex: "employeeNo" },
          { title: "姓名", dataIndex: "name" },
          { title: "角色", dataIndex: "roleLabel" },
          { title: "状态", dataIndex: "status", render: (value: string) => <Tag color={value === "ACTIVE" ? "success" : "default"}>{value === "ACTIVE" ? "在职" : "停用"}</Tag> },
          { title: "最近登录", dataIndex: "lastLoginAt", render: (value: string | null) => formatWhen(value) },
          {
            title: "操作",
            render: (_v: unknown, row: AccountRow) => (
              <Space>
                <Button onClick={() => setRenameRow(row)}>修改这个姓名</Button>
                <Button disabled={row.id === user?.id} onClick={() => setRoleRow(row)}>改成这个角色</Button>
                <Button danger disabled={row.id === user?.id || row.status !== "ACTIVE"} onClick={() => setDisableId(row.id)}>
                  停用这个账号
                </Button>
                <Button onClick={() => setResetId(row.id)}>重置密码并要求下次修改</Button>
              </Space>
            ),
          },
        ]}
      />
      {user ? (
        <>
          <p>不能停用你正在使用的这个账号。请换另一个管理员操作。</p>
          <p>不能改你正在使用的这个账号的角色。请换另一个管理员操作。</p>
        </>
      ) : null}
      <Modal
        destroyOnClose
        open={createOpen}
        title="开通账号"
        okText="开通这个账号"
        cancelText="放弃并返回"
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
      >
        <p>对方第一次登录必须改成自己的密码。</p>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            void createUser(values)
              .then(() => {
                message.success("账号已开通。请把工号和临时密码交给本人，不要共用。");
                setCreateOpen(false);
                form.resetFields();
                load();
              })
              .catch((error: unknown) => message.error(error instanceof Error ? error.message : "账号没有改成。"));
          }}
        >
          <Form.Item label="工号" name="employeeNo" rules={[{ required: true, message: "工号还空着。" }]}><Input /></Form.Item>
          <Form.Item label="姓名" name="name" rules={[{ required: true, message: "姓名还空着。" }]}><Input /></Form.Item>
          <Form.Item label="角色" name="role" rules={[{ required: true, message: "请选择角色。" }]}>
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item label="初始密码" name="password" rules={[{ required: true, min: 8, message: "密码少于 8 位。请至少输入 8 位。" }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        destroyOnClose
        open={disableId !== null}
        title="停用这个账号"
        okText="停用并保留历史记录"
        okButtonProps={{ danger: true }}
        cancelText="先不操作"
        onCancel={() => setDisableId(null)}
        onOk={() => {
          if (!disableId) return;
          void disableUser(disableId).then(() => {
            message.success("已停用。此人不能再登录，过去的记录还在。");
            setDisableId(null);
            load();
          });
        }}
      >
        停用后不能登录。已经发生的操作记录仍留在时光轴里，不会删。
      </Modal>
      <Modal
        destroyOnClose
        open={resetId !== null}
        title="重置密码"
        okText="保存临时密码"
        cancelText="先不操作"
        onCancel={() => setResetId(null)}
        onOk={() => {
          if (!resetId || tempPassword.length < 8) {
            message.warning("密码少于 8 位。请至少输入 8 位。");
            return Promise.reject(new Error("short"));
          }
          return resetPassword(resetId, tempPassword).then(() => {
            message.success("把临时密码当面告诉本人。对方下次登录要改成自己的密码。");
            setResetId(null);
            setTempPassword("");
          });
        }}
      >
        <Input.Password value={tempPassword} onChange={(event) => setTempPassword(event.target.value)} placeholder="临时密码" />
      </Modal>
      <Modal
        destroyOnClose
        open={renameRow !== null}
        title="修改姓名"
        okText="保存这个姓名"
        cancelText="先不改了"
        onCancel={() => setRenameRow(null)}
        onOk={() => {
          const target = renameRow;
          if (!target) return;
          return renameForm.validateFields().then((values) =>
            renameUser(target.id, values.name).then(async (saved) => {
              setRows((current) => current.map((row) => (row.id === saved.id ? { ...row, name: saved.name } : row)));
              const self = user?.id === saved.id;
              if (self) {
                const me = await fetchMe();
                replaceUser(me.user);
              }
              message.success(self ? "姓名已保存。右上角和列表都换成了新姓名。" : "姓名已保存。列表里已经换成新姓名。");
              window.setTimeout(() => setRenameRow(null), 0);
            })
          ).catch((error: unknown) => {
            if (isFieldError(error)) return Promise.reject(error);
            const text = error instanceof Error ? error.message : "姓名没有改成。请核对后再保存一次。";
            message.error(text);
            return Promise.reject(error instanceof Error ? error : new Error(text));
          });
        }}
      >
        <p>只改姓名。工号、角色和密码都保持原样。</p>
        <Form form={renameForm} layout="vertical" initialValues={{ name: renameRow?.name ?? "" }}>
          <Form.Item
            label="姓名"
            name="name"
            rules={[
              { required: true, whitespace: true, message: EMPTY_PERSON_NAME_MESSAGE },
              { type: "string", max: PERSON_NAME_MAX_LENGTH, message: LONG_PERSON_NAME_MESSAGE },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        destroyOnClose
        open={roleRow !== null}
        title="改成这个角色"
        okText="保存这个角色"
        cancelText="先不改了"
        onCancel={() => setRoleRow(null)}
        onOk={() => {
          const target = roleRow;
          if (!target) return;
          return roleForm.validateFields().then((values) =>
            changeUserRole(target.id, values.role).then((saved) => {
              setRows((current) =>
                current.map((row) =>
                  row.id === saved.id ? { ...row, role: saved.role, roleLabel: saved.roleLabel } : row
                )
              );
              message.success("角色已保存。对方刷新页面后，左侧菜单会换成新角色能看到的内容。");
              window.setTimeout(() => setRoleRow(null), 0);
            })
          ).catch((error: unknown) => {
            if (isFieldError(error)) return Promise.reject(error);
            const text = error instanceof Error ? error.message : "角色没有改成。请核对后再保存一次。";
            message.error(text);
            return Promise.reject(error instanceof Error ? error : new Error(text));
          });
        }}
      >
        <p>换角色后，这个人能做的事会马上按新角色计算。工号、姓名和密码不变。</p>
        <Form form={roleForm} layout="vertical" initialValues={{ role: roleRow?.role ?? "OPERATOR" }}>
          <Form.Item label="角色" name="role" rules={[{ required: true, message: "请选择角色。" }]}>
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
